import { auth, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

// Types for the response data from our cloud functions
// This should match PollyVoice interface in functions/src/polly.ts
export interface PollyVoice {
  voiceURI: string;
  name: string;
  lang: string;
  localService: boolean;
  default: boolean;
}

export interface Viseme {
  time: number;
  type: string;
  value: string;
}

export interface AmplitudePoint {
  time: number;
  value: number;
}

export interface PollyServiceResponse {
  audio: string;
  visemes: Viseme[];
  amplitudeData: AmplitudePoint[];
  extrema: AmplitudePoint[];
}

export const PollyService = {
  fetchPollyVoices: async (): Promise<PollyVoice[]> => {
    try {
      if (!auth.currentUser || auth.currentUser.isAnonymous) {
        console.debug(
          "PollyService: User not authenticated, skipping remote Polly voices",
        );
        return [];
      }
      const getPollyVoices = httpsCallable<unknown, PollyVoice[]>(
        functions,
        "pollyVoices",
      );
      const result = await getPollyVoices();
      console.log("PollyService: fetchPollyVoices result:", result);

      if (result.data && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string } | null;
      if (
        err?.code === "functions/unauthenticated" ||
        String(err?.message || "").includes(
          "must be called while authenticated",
        )
      ) {
        console.debug(
          "PollyService: pollyVoices requires authenticated user; skipping.",
        );
        return [];
      }
      console.error("Error fetching Polly voices from backend:", error);
      return [];
    }
  },

  synthesizeSpeech: async (
    text: string,
    voiceId: string,
    engine?: string,
  ): Promise<PollyServiceResponse> => {
    try {
      const synthesizeParams = { text, voiceId, engine };

      const synthesize = httpsCallable<
        typeof synthesizeParams,
        PollyServiceResponse
      >(functions, "pollySynthesize");
      console.log(
        "PollyService: Calling pollySynthesize with:",
        synthesizeParams,
      );
      const result = await synthesize(synthesizeParams);
      console.log("PollyService: pollySynthesize result:", result);

      if (result.data && result.data.audio) {
        const audioBase64 = result.data.audio;
        const blob = await (
          await fetch(`data:audio/mp3;base64,${audioBase64}`)
        ).blob();
        return {
          ...result.data,
          audio: URL.createObjectURL(blob),
          visemes: result.data.visemes || [],
          amplitudeData: result.data.amplitudeData || [],
          extrema: result.data.extrema || [],
        };
      }
      throw new Error("No audio returned from backend");
    } catch (error) {
      console.error("Error synthesizing speech with Polly backend:", error);
      throw error;
    }
  },
};
